/**
 * FontManager — painterly typeface registry and text-style presets.
 *
 * Font selection (Ghibli 70s aesthetic):
 *   Primary:    "Almendra"     — hand-crafted calligraphic serif with organic
 *               letterforms; warm, readable, fantasy-appropriate.
 *   Accessible: "OpenDyslexic" — maximises legibility for dyslexic players;
 *               loaded from /fonts/OpenDyslexic-Regular.woff2 (self-hosted).
 *
 * Accessibility settings (§18.4) are persisted in localStorage under the key
 * `game_accessibility`. Call FontManager.refresh() after writing new settings
 * so newly-created text objects pick up the latest preferences.
 *
 * Usage:
 *   // basic
 *   const text = scene.add.text(x, y, '', FontManager.style('body'));
 *
 *   // with per-instance override (e.g. word-wrap width set at layout time)
 *   const body = scene.add.text(x, y, '', FontManager.style('body', { wordWrap: { width: 300 } }));
 *
 *   // reading/writing accessibility prefs
 *   FontManager.save({ dyslexicFont: true });  // persists and refreshes
 *   FontManager.getSettings().largeText;        // read current value
 */

// ── Font family strings ───────────────────────────────────────────────────────

/** Primary painterly font — loaded via Google Fonts `<link>` in index.html. */
export const FONT_PAINTERLY = "'Almendra', serif";

/** Accessible alternative for dyslexic players — self-hosted in /fonts/. */
export const FONT_ACCESSIBLE = "'OpenDyslexic', sans-serif";

/** Retained for debug / performance overlays only. */
export const FONT_DEBUG = 'monospace';

// ── Accessibility settings ────────────────────────────────────────────────────

const A11Y_KEY = 'game_accessibility';

export interface AccessibilitySettings {
  /** Replace Almendra with OpenDyslexic across all UI text. */
  dyslexicFont: boolean;
  /** Scale all text up by 30% for improved readability. */
  largeText: boolean;
  /** Add black stroke behind dialogue body/hint text for subtitle contrast. */
  highContrastSubtitles: boolean;
}

const A11Y_DEFAULTS: AccessibilitySettings = {
  dyslexicFont: false,
  largeText: false,
  highContrastSubtitles: false,
};

// ── Text style roles ──────────────────────────────────────────────────────────

/**
 * Semantic text roles used across all UI systems.
 * Each role defines a base size, colour, and weight that is applied
 * consistently to every matching text object in the game.
 */
export type TextRole =
  | 'body'      // Dialogue body, descriptions                — 12 px
  | 'name'      // NPC / player name labels                   — 13 px bold
  | 'hint'      // Inline cues ([E] continue, key hints)      — 10 px
  | 'choice'    // Dialogue choice button labels              — 11 px
  | 'hud'       // Ability letters, cooldown labels           — 10 px bold
  | 'hud-sm'    // Tiny bar stat labels (HP / MP values)      —  9 px
  | 'title'     // Panel/overlay titles, results headers      — 18 px bold
  | 'label'     // World-space NPC / enemy name tags          — 11 px
  | 'tooltip'   // Item tooltips, key-binding badges          — 11 px
  | 'debug';    // Performance overlay (stays monospace)      — 11 px

// ── Internal base style table ─────────────────────────────────────────────────

interface BaseStyle {
  basePx: number;
  color: string;
  bold?: true;
}

const BASE: Record<TextRole, BaseStyle> = {
  body:      { basePx: 12, color: '#d4d4e4' },
  name:      { basePx: 13, color: '#e8c44a', bold: true },
  hint:      { basePx: 10, color: '#8b8ba8' },
  choice:    { basePx: 11, color: '#d4d4e4' },
  hud:       { basePx: 10, color: '#d4d4e4', bold: true },
  'hud-sm':  { basePx:  9, color: '#d4d4e4' },
  title:     { basePx: 18, color: '#e8c44a', bold: true },
  label:     { basePx: 11, color: '#d4d4e4' },
  tooltip:   { basePx: 11, color: '#d4d4e4' },
  debug:     { basePx: 11, color: '#d4d4e4' },
};

// ── Phaser text style shape (avoids importing full Phaser at runtime) ─────────

export interface PhaserTextStyle {
  fontFamily?: string;
  fontSize?: string;
  color?: string;
  fontStyle?: string;
  stroke?: string;
  strokeThickness?: number;
  wordWrap?: { width: number; useAdvancedWrap?: boolean };
  align?: string;
  [key: string]: unknown;
}

// ── FontManager (singleton-style static object) ───────────────────────────────

export const FontManager = {
  _settings: { ...A11Y_DEFAULTS } as AccessibilitySettings,

  /** (Re-)load accessibility settings from localStorage. Called on init. */
  refresh(): void {
    try {
      const raw = localStorage.getItem(A11Y_KEY);
      if (!raw) { this._settings = { ...A11Y_DEFAULTS }; return; }
      const p = JSON.parse(raw) as Partial<AccessibilitySettings>;
      this._settings = {
        dyslexicFont: p.dyslexicFont ?? A11Y_DEFAULTS.dyslexicFont,
        largeText: p.largeText ?? A11Y_DEFAULTS.largeText,
        highContrastSubtitles: p.highContrastSubtitles ?? A11Y_DEFAULTS.highContrastSubtitles,
      };
    } catch {
      this._settings = { ...A11Y_DEFAULTS };
    }
  },

  /**
   * Persist one or more accessibility settings and refresh internal state.
   * Any UI text objects that are created after this call will use the new
   * settings automatically.  Existing live text objects must call setText()
   * or be re-created to reflect the change — typically handled at scene
   * restart or via explicit update hooks in each UI system.
   */
  save(updates: Partial<AccessibilitySettings>): void {
    this._settings = { ...this._settings, ...updates };
    try {
      localStorage.setItem(A11Y_KEY, JSON.stringify(this._settings));
    } catch { /* localStorage unavailable (private browsing / quota) */ }
  },

  /** Read-only snapshot of the current accessibility settings. */
  getSettings(): Readonly<AccessibilitySettings> {
    return this._settings;
  },

  /**
   * Scale a pixel size by the current large-text multiplier (×1.3 when enabled)
   * and return it as a CSS size string (e.g. `"9px"`).  Use this when you need
   * to pass a size override to `style()` so that the large-text preference is
   * honoured even when the override differs from the role's default.
   *
   * Example: `FontManager.style('hint', { fontSize: FontManager.px(7) })`
   */
  px(basePx: number): string {
    const scale = this._settings.largeText ? 1.3 : 1.0;
    return `${Math.max(8, Math.round(basePx * scale))}px`;
  },

  /**
   * The font family string to use for a given role.
   * Debug role always uses monospace regardless of accessibility settings.
   */
  fontFamily(role: TextRole = 'body'): string {
    if (role === 'debug') return FONT_DEBUG;
    return this._settings.dyslexicFont ? FONT_ACCESSIBLE : FONT_PAINTERLY;
  },

  /**
   * Build a Phaser text style object for the given semantic role.
   *
   * - Font family reflects current accessibility settings.
   * - Font size is scaled by ×1.3 when `largeText` is enabled; minimum 8 px.
   * - `highContrastSubtitles` adds a solid black stroke behind `body`/`hint`
   *   text so dialogue remains readable against any background.
   *
   * @param role     - Semantic text role (body, name, hud, title, …)
   * @param overrides - Optional per-instance overrides (e.g. wordWrap width,
   *                    a different color for a specific element, etc.)
   */
  style(role: TextRole, overrides?: PhaserTextStyle): PhaserTextStyle {
    const base = BASE[role];
    const scale = this._settings.largeText ? 1.3 : 1.0;
    const px = Math.max(8, Math.round(base.basePx * scale));

    const style: PhaserTextStyle = {
      fontFamily: this.fontFamily(role),
      fontSize: `${px}px`,
      color: base.color,
    };

    if (base.bold) style.fontStyle = 'bold';

    // High-contrast subtitles: solid black stroke behind dialogue text
    if (this._settings.highContrastSubtitles && (role === 'body' || role === 'hint')) {
      style.color = '#ffffff';
      style.stroke = '#000000';
      style.strokeThickness = 3;
    }

    return overrides ? { ...style, ...overrides } : style;
  },
};

// Eagerly load settings so the first call to style() is correct.
FontManager.refresh();
